import { useEffect, useRef, useState } from "react";

/**
 * CapCut-style scrubbable video timeline.
 *
 * Props:
 *   videoRef       – ref to the <video> element rendered elsewhere
 *   duration       – video duration in seconds
 *   trimStart      – trim in-point (seconds)
 *   trimEnd        – trim out-point (seconds)
 *   maxDurationSec – trim window cap (e.g. 15)
 *   onTrimStart    – (v: number) => void
 *   onTrimEnd      – (v: number) => void
 *   src            – blob/http url of the source video (used to extract thumbnails off-screen)
 *   thumbCount     – how many frame thumbnails to render along the strip (default 10)
 */
export default function Timeline({
  videoRef,
  duration,
  trimStart,
  trimEnd,
  maxDurationSec = 15,
  onTrimStart,
  onTrimEnd,
  src,
  thumbCount = 10,
}) {
  const [thumbs, setThumbs] = useState([]); // data URLs
  const [thumbsLoading, setThumbsLoading] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [dragging, setDragging] = useState(null); // 'in' | 'out' | 'seek' | null
  const trackRef = useRef(null);

  // Sync playhead with the actual video element
  useEffect(() => {
    const v = videoRef?.current;
    if (!v) return;
    let raf;
    const tick = () => {
      if (v) setPlayhead(v.currentTime || 0);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [videoRef]);

  // Extract N frame thumbnails from the source using a hidden <video> + canvas
  useEffect(() => {
    if (!src || !duration || duration <= 0) return;
    let cancelled = false;
    setThumbsLoading(true);
    setThumbs([]);

    const off = document.createElement("video");
    off.src = src;
    off.crossOrigin = "anonymous";
    off.muted = true;
    off.playsInline = true;
    off.preload = "auto";

    const canvas = document.createElement("canvas");
    const W = 80, H = 48;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    const seekTo = (t) => new Promise((resolve) => {
      const onSeeked = () => { off.removeEventListener("seeked", onSeeked); resolve(); };
      off.addEventListener("seeked", onSeeked);
      off.currentTime = Math.min(Math.max(0, t), Math.max(0, duration - 0.05));
    });

    const run = async () => {
      try {
        // Wait for metadata
        await new Promise((res, rej) => {
          const onMeta = () => { off.removeEventListener("loadedmetadata", onMeta); res(); };
          off.addEventListener("loadedmetadata", onMeta);
          off.addEventListener("error", () => rej(new Error("video load failed")));
        });
        const out = [];
        for (let i = 0; i < thumbCount; i++) {
          if (cancelled) return;
          const t = (i / Math.max(1, thumbCount - 1)) * duration;
          await seekTo(t);
          try {
            ctx.drawImage(off, 0, 0, W, H);
            out.push(canvas.toDataURL("image/jpeg", 0.55));
          } catch {
            // Cross-origin blocks canvas readback → give up, silent
            return;
          }
          setThumbs([...out]);
        }
      } catch {
        // Silent — timeline still works without thumbnails, just plain colour strip
      } finally {
        if (!cancelled) setThumbsLoading(false);
      }
    };
    run();

    return () => { cancelled = true; off.src = ""; };
  }, [src, duration, thumbCount]);

  // -------- Pointer handlers --------
  const timeFromEvent = (e) => {
    const el = trackRef.current;
    if (!el || !duration) return 0;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0]?.clientX) || 0) - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    return pct * duration;
  };

  const onPointerDown = (which) => (e) => {
    e.preventDefault();
    setDragging(which);
    if (which === "seek") {
      const t = timeFromEvent(e);
      // Clamp seek within the trim window for muscle-memory feel
      const clamped = Math.min(trimEnd, Math.max(trimStart, t));
      const v = videoRef?.current;
      if (v) v.currentTime = clamped;
    }
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const t = timeFromEvent(e);
      if (dragging === "in") {
        // Keep at least 0.5s gap and enforce max window
        const newStart = Math.max(0, Math.min(t, trimEnd - 0.5));
        const capped = Math.max(newStart, trimEnd - maxDurationSec);
        onTrimStart?.(capped);
      } else if (dragging === "out") {
        const newEnd = Math.max(trimStart + 0.5, Math.min(duration, t));
        const capped = Math.min(newEnd, trimStart + maxDurationSec);
        onTrimEnd?.(capped);
      } else if (dragging === "seek") {
        const clamped = Math.min(trimEnd, Math.max(trimStart, t));
        const v = videoRef?.current;
        if (v) v.currentTime = clamped;
      }
    };
    const onUp = () => setDragging(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, duration, trimStart, trimEnd, maxDurationSec, onTrimStart, onTrimEnd, videoRef]);

  if (!duration || duration <= 0) return null;

  const startPct = (trimStart / duration) * 100;
  const endPct = (trimEnd / duration) * 100;
  const playPct = (playhead / duration) * 100;
  const selectedDur = Math.max(0, trimEnd - trimStart);
  const atCap = selectedDur >= maxDurationSec - 0.05;

  return (
    <div className="w-full px-3 py-3 bg-[#08080B] border-t border-white/5" data-testid="video-timeline">
      {/* Meta strip */}
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-2 px-1">
        <span className="tabular-nums">{fmt(playhead)} <span className="text-zinc-700">/</span> {fmt(duration)}</span>
        <span className={`tabular-nums ${atCap ? "text-nexus-pink" : "text-zinc-400"}`}>
          {fmt(selectedDur)} selected · Max {maxDurationSec}s
        </span>
      </div>

      {/* Track */}
      <div
        ref={trackRef}
        onPointerDown={onPointerDown("seek")}
        className="relative h-14 rounded-lg overflow-hidden bg-black/60 cursor-pointer select-none touch-none"
        style={{ userSelect: "none" }}
      >
        {/* Thumbnails row */}
        <div className="absolute inset-0 flex">
          {(thumbs.length ? thumbs : Array.from({ length: thumbCount })).map((t, i) => (
            <div
              key={i}
              className="flex-1 bg-cover bg-center relative"
              style={t ? { backgroundImage: `url(${t})` } : { background: `linear-gradient(90deg, rgba(139,92,246,0.14), rgba(59,130,246,0.14))` }}
            >
              {thumbsLoading && !t && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/[0.03] via-white/[0.06] to-white/[0.03] animate-pulse" />
              )}
            </div>
          ))}
        </div>

        {/* Outside-trim dimming (before IN) */}
        <div
          className="absolute inset-y-0 left-0 bg-black/70 pointer-events-none"
          style={{ width: `${startPct}%` }}
        />
        {/* Outside-trim dimming (after OUT) */}
        <div
          className="absolute inset-y-0 right-0 bg-black/70 pointer-events-none"
          style={{ width: `${100 - endPct}%` }}
        />

        {/* Selected window border */}
        <div
          className="absolute inset-y-0 border-y-2 border-nexus-pink pointer-events-none"
          style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
        />

        {/* IN handle */}
        <button
          type="button"
          onPointerDown={(e) => { e.stopPropagation(); onPointerDown("in")(e); }}
          className="absolute inset-y-0 w-4 flex items-center justify-center bg-nexus-pink cursor-ew-resize z-10 rounded-l-lg"
          style={{ left: `calc(${startPct}% - 8px)` }}
          data-testid="timeline-in-handle"
          aria-label="Trim start"
        >
          <span className="h-6 w-0.5 bg-white/90 rounded-full" />
        </button>
        {/* OUT handle */}
        <button
          type="button"
          onPointerDown={(e) => { e.stopPropagation(); onPointerDown("out")(e); }}
          className="absolute inset-y-0 w-4 flex items-center justify-center bg-nexus-pink cursor-ew-resize z-10 rounded-r-lg"
          style={{ left: `calc(${endPct}% - 8px)` }}
          data-testid="timeline-out-handle"
          aria-label="Trim end"
        >
          <span className="h-6 w-0.5 bg-white/90 rounded-full" />
        </button>

        {/* Playhead */}
        {playhead >= trimStart && playhead <= trimEnd && (
          <div
            className="absolute inset-y-0 w-0.5 bg-white pointer-events-none z-20 shadow-[0_0_10px_rgba(255,255,255,0.9)]"
            style={{ left: `calc(${playPct}% - 1px)` }}
            data-testid="timeline-playhead"
          >
            <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 rounded-full bg-white" />
          </div>
        )}
      </div>

      {/* Playback controls hint */}
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-zinc-600 mt-2 px-1">
        <span>Drag handles to trim · Tap track to scrub</span>
        {atCap && <span className="text-nexus-pink">Max window reached</span>}
      </div>
    </div>
  );
}

function fmt(s) {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1);
  const secStr = sec.padStart(4, "0");
  return `${m}:${secStr}`;
}
